import { ref, computed, watch } from 'vue'
import { currentTheme, refreshTheme } from 'devextreme/viz/themes'

const STORAGE_KEY = 'gessner-theme'
const LIGHT_THEME = 'dx.fluent.blue.light'
const DARK_THEME = 'dx.fluent.blue.dark'

const stored = (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || 'system'
const mode = ref(stored)

const mql = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

function getThemeName(m) {
    if (m === 'system') return mql && mql.matches ? DARK_THEME : LIGHT_THEME
    return m === 'dark' ? DARK_THEME : LIGHT_THEME
}

function applyTheme(themeName) {
    if (typeof document === 'undefined') return
    const links = document.querySelectorAll('link[data-dx-theme]')
    links.forEach((link) => {
        const isMatch = link.getAttribute('data-dx-theme') === themeName
        link.media = isMatch ? 'all' : 'none'
    })
    document.documentElement.setAttribute('data-dx-theme', themeName)
    try {
        currentTheme(themeName)
        refreshTheme()
    } catch {
        // Charts not yet loaded — ignore
    }
}

const themeName = computed(() => getThemeName(mode.value))
const isDark = computed(() => themeName.value === DARK_THEME)

watch(
    themeName,
    (name) => {
        applyTheme(name)
    },
    { immediate: false }
)

if (mql) {
    const listener = () => {
        if (mode.value === 'system') applyTheme(getThemeName('system'))
    }
    if (mql.addEventListener) mql.addEventListener('change', listener)
    else if (mql.addListener) mql.addListener(listener)
}

function bootstrapTheme() {
    applyTheme(themeName.value)
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootstrapTheme, { once: true })
    } else {
        bootstrapTheme()
    }
}

export function useTheme() {
    function setMode(next) {
        mode.value = next
        try {
            localStorage.setItem(STORAGE_KEY, next)
        } catch {
            // ignore storage failures (private mode etc.)
        }
    }

    function toggle() {
        setMode(isDark.value ? 'light' : 'dark')
    }

    return {
        mode,
        themeName,
        isDark,
        setMode,
        toggle,
    }
}

export { bootstrapTheme }
